import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { StringFormatter } from '@repo/toolkit/string'
import Badge from '@repo/ui/badge'
import { UserFourIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useMemo } from 'react'

import { MissionTypeEnum } from '../../../lib/types/enums/mission/mission-type.enum'
import { MissionPartial } from '../../../lib/types/models/mission/mission'
import { formatPhoneNumber } from '../../../utils/functions/format-phone-number'
import InformationToolTip from '../../elements/information-tooltip'

interface StaffDetails {
  firstName: string
  lastName: string
  phoneNumber: string | null
  email: string
}

interface MissionRowDisplayProps {
  mission: MissionPartial
  onIdClick: (id: number) => void
}

const MissionRowDisplay: React.FC<MissionRowDisplayProps> = ({ mission, onIdClick }) => {
  const getStaffDetails = (mission: MissionPartial): StaffDetails[] => {
    switch (mission.type) {
      case MissionTypeEnum.EXTERNAL:
        return mission.affectedExternalStaffs.map((staff) => ({
          firstName: staff.firstName,
          lastName: staff.lastName,
          phoneNumber: staff.phone,
          email: staff.email
        }))

      case MissionTypeEnum.OFFICE:
        return mission.affectedLecStaffs.map((staff) => ({
          firstName: staff.user.firstName,
          lastName: staff.user.lastName,
          phoneNumber: staff.user.phoneNumber ?? null,
          email: staff.user.email
        }))

      case MissionTypeEnum.STAFF:
        return mission.affectedStaffs.map((staff) => ({
          firstName: staff.staff.firstName,
          lastName: staff.staff.lastName,
          phoneNumber: staff.staff.phoneNumber,
          email: staff.staff.user.email
        }))

      default:
        return []
    }
  }

  const staffs = useMemo(() => getStaffDetails(mission), [mission])
  const missingCount = Math.max(0, mission.requiredNumberOfStaff - staffs.length)

  return (
    <Badge
      variant="white"
      className="h-9 rounded"
      text={
        <div className="flex items-center gap-3">
          <Typography.Link onClick={() => onIdClick(mission.id)} variant="label-medium">
            {mission.title}
          </Typography.Link>
          {mission.requiredNumberOfStaff > 0 && (
            <div className="flex items-center gap-2">
              {staffs.map((staff, index) => (
                <InformationToolTip
                  key={index}
                  icon={
                    <div className={'bg-green-light rounded-sm'}>
                      <UserFourIcon color={tailwindColors.green.DEFAULT} light />
                    </div>
                  }
                >
                  <div className="w-56 space-y-1">
                    <Typography
                      variant="label-medium"
                      color={tailwindColors.white.DEFAULT}
                      className="truncate"
                    >
                      <strong>NOM : </strong> {StringFormatter.capitalize(staff.firstName)}{' '}
                      {staff.lastName.toUpperCase()}
                    </Typography>
                    <Typography
                      variant="label-medium"
                      color={tailwindColors.white.DEFAULT}
                      className="truncate"
                    >
                      <strong>Téléphone : </strong>{' '}
                      {staff.phoneNumber ? formatPhoneNumber(staff.phoneNumber) : '-'}
                    </Typography>
                    <Typography
                      variant="label-medium"
                      color={tailwindColors.white.DEFAULT}
                      className="truncate"
                    >
                      <strong>E-mail : </strong>
                      <a href={`mailto:${staff.email}`} className="text-blue">
                        {staff.email}
                      </a>
                    </Typography>
                  </div>
                </InformationToolTip>
              ))}
              {Array.from({ length: missingCount }).map((_, index) => (
                <div key={`missing-${index}`} className={'bg-red-light rounded-sm'}>
                  <UserFourIcon color={tailwindColors.red.DEFAULT} light />
                </div>
              ))}
            </div>
          )}
        </div>
      }
    />
  )
}

export default MissionRowDisplay
